import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';
import { formatMoney } from '../../lib/format.js';
import type { OwnRateSheet as Sheet } from '@arcnet/contracts';
import { useRateSheetRead } from './useRateSheetRead.js';
import { SheetReadView } from './SheetReadView.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * What the client's own carrier charges them (0131, owner 26 September 2026): a sheet uploaded for
 * this trunk, used to show the carrier's cost on every call over it. Never charged by ArcNet.
 *
 * Read in the browser with the same parser the API validates against, so the preview is what is
 * stored. CSV, since every spreadsheet saves one; a carrier's title rows above the header are fine.
 */
export function OwnRateSheet({ tenantId, trunkId }: { tenantId: string; trunkId: string }) {
  const can = useSession((s) => s.can);
  const mayWrite = can('connectivity:write');
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/own-trunks/${trunkId}/rates`;
  const sheet = useQuery({ queryKey: ['own-rates', trunkId], queryFn: () => api.get<Sheet | { deck_id: null }>(base) });
  const current = sheet.data && sheet.data.deck_id ? sheet.data as Sheet : null;

  // A PDF, a spreadsheet or a CSV, read for a preview before anything is stored (useRateSheetRead).
  const reader = useRateSheetRead(`${base}/read`, `own:${trunkId}`);
  const upload = useMutation({
    mutationFn: () => api.post<Sheet>(base, { rules: reader.ok!.rules, ...(reader.file ? { file_name: reader.file } : {}) }),
    onSuccess: () => { reader.reset(); void qc.invalidateQueries({ queryKey: ['own-rates', trunkId] }); },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(base),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['own-rates', trunkId] }),
  });

  const [number, setNumber] = useState('');
  const [asked, setAsked] = useState('');
  const lookup = useQuery({
    queryKey: ['own-rates-lookup', trunkId, asked, current?.version],
    queryFn: () => api.get<{ number: string; found: boolean; currency?: string; one_minute?: string;
      rule?: { prefix: string; description: string | null; rate_per_minute: string; connection_fee: string; first_increment_sec: number; subsequent_increment_sec: number } | null }>(
      `${base}/lookup?number=${encodeURIComponent(asked)}`),
    enabled: !!current && asked.length >= 2,
  });

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong style={{ fontSize: 13 }}>Carrier rates</strong>
        <span className="arc-muted" style={{ fontSize: 12.5, flex: '1 1 260px' }}>
          {sheet.isLoading ? '…' : current
            ? `${current.rule_count.toLocaleString()} destinations${current.file_name ? ` from ${current.file_name}` : ''}, since ${new Date(current.effective_from).toLocaleDateString()}. Calls over this trunk show what the carrier charges.`
            : 'No rate sheet: calls over this trunk show no cost. Upload your carrier’s sheet to see it per call. ArcNet does not charge these calls.'}
        </span>
        {mayWrite && (
          <label className="arc-btn arc-btn-sm" style={{ cursor: 'pointer' }}>
            {current ? 'Upload a new sheet' : 'Upload sheet'}
            <input type="file" accept=".csv,.txt,.xlsx,.xls,.pdf" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void reader.read(f); e.target.value = ''; }} />
          </label>
        )}
        {mayWrite && current && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={remove.isPending}
            onClick={() => { void confirmAction({ title: 'Stop showing carrier costs for this trunk?', body: 'Calls already priced keep their cost.', action: 'Stop showing', danger: true }).then((ok) => { if (ok) remove.mutate(); }); }}>
            Remove
          </button>
        )}
      </div>
      {sheet.error && <ErrorBanner error={sheet.error} />}
      {remove.error && <ErrorBanner error={remove.error} />}

      <SheetReadView reader={reader} currency={current?.currency ?? 'ZAR'} useLabel="Use this sheet"
        pending={upload.isPending} error={upload.error} onUse={() => upload.mutate()} />

      {current && (
        <div className="arc-row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <input className="arc-input arc-mono" style={{ maxWidth: 200 }} placeholder="082 123 4567" value={number}
            aria-label="Number to price" onChange={(e) => setNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setAsked(number.trim()); }} />
          <button type="button" className="arc-btn arc-btn-sm" disabled={number.trim().length < 2} onClick={() => setAsked(number.trim())}>
            What does it cost?
          </button>
          {lookup.data && (
            <span className="arc-muted" style={{ fontSize: 12.5 }}>
              {lookup.data.found && lookup.data.rule
                ? <>{formatMoney(lookup.data.rule.rate_per_minute, lookup.data.currency ?? 'ZAR')} a minute
                    {lookup.data.rule.description ? ` (${lookup.data.rule.description}, ` : ' ('}prefix {lookup.data.rule.prefix})
                    {lookup.data.rule.first_increment_sec !== 1 || lookup.data.rule.subsequent_increment_sec !== 1
                      ? `, billed ${lookup.data.rule.first_increment_sec}/${lookup.data.rule.subsequent_increment_sec} s` : ', per second'}.</>
                : `No rate for ${lookup.data.number} on the sheet: such calls show no cost.`}
            </span>
          )}
          {lookup.error && <ErrorBanner error={lookup.error} />}
        </div>
      )}
    </div>
  );
}
